import { useEffect, useMemo, useState } from 'react';
import Logo from './Logo';
import { ApiError, createStripeCheckout, fetchMe, type CurrentUser } from '../lib/api';

type PlanId = 'solo' | 'agency';

const PLAN_META: Record<
  PlanId,
  { name: string; price: string; period: string; tagline: string; keyAllowance: string }
> = {
  solo: {
    name: 'Solo',
    price: '$5',
    period: '/month',
    tagline: 'For freelancers tracking a handful of projects.',
    keyAllowance: 'Up to 3 inbound tokens',
  },
  agency: {
    name: 'Agency',
    price: '$20',
    period: '/month',
    tagline: 'For studios billing multiple clients every month.',
    keyAllowance: 'Up to 25 inbound tokens',
  },
};

export default function Checkout() {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const planFromUrl = params.get('plan');
  const cancelled = params.get('cancelled') === '1';
  const initialPlan: PlanId =
    planFromUrl === 'solo' || planFromUrl === 'agency' ? planFromUrl : 'agency';

  const [selected, setSelected] = useState<PlanId>(initialPlan);

  useEffect(() => {
    fetchMe()
      .then((currentUser) => {
        setUser(currentUser);
        if (currentUser.hasActiveSubscription) {
          window.location.replace('/app');
        }
      })
      .catch((caught) => {
        if (caught instanceof ApiError && caught.status === 401) {
          window.location.href = '/_auth/google/login';
          return;
        }
        setError(caught?.message ?? 'Could not load your account.');
      });
  }, []);

  const selectedMeta = useMemo(() => PLAN_META[selected], [selected]);

  async function handleCheckout() {
    setSubmitting(true);
    setError(null);
    try {
      const { url } = await createStripeCheckout(selected);
      window.location.assign(url);
    } catch (caught) {
      setSubmitting(false);
      setError(caught instanceof Error ? caught.message : 'Checkout failed.');
    }
  }

  if (!user && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="block-brutal p-8 max-w-md text-center">
          <Logo className="h-10 w-10 mx-auto mb-4" />
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-spray-magenta font-bold mb-3">
            // LOADING
          </div>
          <p className="text-foreground leading-relaxed">One moment — fetching your account…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="container-ledger h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5 text-foreground">
            <Logo className="w-7 h-7" />
            <span className="text-xl tracking-tight">Key Receipt</span>
          </a>
          {user && (
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="hidden sm:inline font-mono text-[12px]">{user.email}</span>
              <a href="/_auth/logout" className="hover:text-foreground transition">
                Sign out
              </a>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1 container-ledger py-14 md:py-20">
        <div className="max-w-3xl mx-auto">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-spray-magenta font-bold">
            // Step 2 of 2
          </div>
          <h1 className="font-display text-4xl md:text-5xl uppercase leading-[0.95] mt-3 font-bold">
            Add a card to finish signing up.
          </h1>
          <p className="mt-4 text-muted-foreground text-lg leading-relaxed max-w-2xl">
            14 days free. We bill monthly thereafter. Cancel anytime in one
            click from your dashboard — no emails to customer support.
          </p>

          {cancelled && (
            <div className="mt-6 border border-primary bg-spray-yellow/5 text-spray-yellow  px-4 py-3 text-sm font-mono">
              Checkout cancelled. Pick a plan below whenever you&rsquo;re ready.
            </div>
          )}

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {(Object.keys(PLAN_META) as PlanId[]).map((planId) => (
              <PlanOption
                key={planId}
                planId={planId}
                selected={selected === planId}
                onSelect={() => setSelected(planId)}
              />
            ))}
          </div>

          <div className="mt-10 bg-spray-yellow text-primary-foreground  p-6">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-primary-foreground/70 font-bold">
                  // You picked
                </div>
                <div className="text-2xl font-bold mt-1 tracking-tight">
                  {selectedMeta.name}{' '}
                  <span className="text-primary-foreground/60 font-normal">
                    · {selectedMeta.price}
                    <span className="text-primary-foreground/40">{selectedMeta.period}</span>
                  </span>
                </div>
                <div className="mt-1 text-primary-foreground/65 text-sm">
                  {selectedMeta.keyAllowance} · 14 days free, then{' '}
                  {selectedMeta.price}
                  {selectedMeta.period}.
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleCheckout}
              disabled={submitting}
              className="mt-6 w-full bg-card text-foreground font-medium px-5 py-3.5  hover:bg-muted transition disabled:opacity-60"
            >
              {submitting ? 'Redirecting to Stripe…' : `Continue to Stripe · ${selectedMeta.price}${selectedMeta.period}`}
            </button>

            {error && (
              <div className="mt-4 text-sm text-primary-foreground font-mono border-2 border-foreground bg-card text-foreground px-3 py-2">
                {error}
              </div>
            )}

            <p className="mt-4 text-[11px] font-mono text-primary-foreground/50 text-center">
              Card secured by Stripe. No charge for 14 days. Cancel anytime.
            </p>
          </div>

          <div className="mt-10 text-center text-sm text-muted-foreground">
            Running a big team?{' '}
            <a
              href="mailto:hi@keyreceipt.com?subject=Key%20Receipt%20Scale%20plan"
              className="text-foreground link-underline"
            >
              Talk to us about Scale
            </a>
            .
          </div>
        </div>
      </main>

      <footer className="border-t border-border bg-card">
        <div className="container-ledger py-6 text-xs font-mono text-muted-foreground flex justify-between">
          <span>Key Receipt · keyreceipt.com</span>
          <a href="/" className="hover:text-foreground transition">
            ← Back to marketing site
          </a>
        </div>
      </footer>
    </div>
  );
}

function PlanOption({
  planId,
  selected,
  onSelect,
}: {
  planId: PlanId;
  selected: boolean;
  onSelect: () => void;
}) {
  const meta = PLAN_META[planId];
  return (
    <button
      type="button"
      onClick={onSelect}
      className={
        'text-left  border p-5 transition ' +
        (selected
          ? 'border-ink bg-card '
          : 'border-border bg-card hover:border-foreground/40')
      }
    >
      <div className="flex items-baseline justify-between">
        <div className="text-xl font-bold tracking-tight">{meta.name}</div>
        <div className="font-mono text-[11px] tracking-wider uppercase text-spray-yellow">
          {selected ? 'Selected' : 'Pick'}
        </div>
      </div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span className="text-3xl font-bold tabular-nums">{meta.price}</span>
        <span className="font-mono text-xs text-muted-foreground">{meta.period}</span>
      </div>
      <div className="mt-3 text-[13px] text-muted-foreground leading-relaxed">
        {meta.tagline}
      </div>
      <div className="mt-3 font-mono text-[11px] tracking-wider uppercase text-spray-yellow">
        {meta.keyAllowance}
      </div>
    </button>
  );
}
